import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import brandingReducer from "../pages/Branding/states/brandingSlice";
// Slices backing the lifted Studies home + TopNavbar preview. studiesSlice is the
// real slice; the rest are inert preview stubs (no backend in this app).
import studiesReducer from "../pages/Studies/states/studiesSlice";
import themeManagementReducer from "../pages/AdminCenter/states/themeManagementSlice";
import unsavedChangesReducer from "../pages/ManageStudies/states/slice/unsavedChangesSlice";
// Real ManageStudies slices — needed once the live-preview carousel mounts the real page.
import manageStudyReducer from "../pages/ManageStudies/states/slice/manageStudySlice";
import manageTranscriptReducer from "../pages/ManageStudies/states/slice/manageTranscriptSlice";
import manageDescriptionGuideReducer from "../pages/ManageStudies/states/slice/manageDescriptionGuideSlice";
// SubHeader (rendered by ManageStudies) reads state.customSegmentSlice.
import customSegmentReducer from "../pages/Insights/states/customSegmentSlice";
import reportsReducer from "../pages/Reports/states/reportsSlice";
import userGuideReducer from "../pages/UserGuide/states/userGuideSlice";
import askKuralReducer from "../pages/NewAskKural/states/askKuralSlice";
import profileContextReducer from "../pages/ProfileContext/states/profileContextSlice";
import tenantsSlice from "src/pages/Tenants/states/tenantsSlice";
import auditSlice from "src/pages/Audit/states/auditSlice";
import accessSlice from "src/pages/Access/states/accessSlice";
import releasesSlice from "src/pages/Releases/states/releasesSlice";
import authSlice from "src/store/authSlice";
// Slices backing the branding live-preview carousel's Insights (deep dive) and
// Ask Kural slides. They have no backend in KCC; the slides seed them from
// captured JSON (see pages/Branding/preview/mockApi.ts + the *Slide components).
import deepDiveInsightsReducer from "../pages/Insights/states/deepDiveInsightslice";
import sectionalSummaryReducer from "../pages/Insights/states/sectionalSummarySlice";
import countWidgetReducer from "../pages/NewAskKural/states/countWidgetSlice";
import widgetReducer from "../pages/NewAskKural/states/widgetSlice";
import governanceReducer from "../pages/NewAskKural/states/governanceSlice";

const rootReducer = combineReducers({
	branding: brandingReducer,
	studiesSlice: studiesReducer,
	themeManagementSlice: themeManagementReducer,
	unsavedChangesSlice: unsavedChangesReducer,
	manageStudySlice: manageStudyReducer,
	manageTranscriptSlice: manageTranscriptReducer,
	manageDescriptionGuideSlice: manageDescriptionGuideReducer,
	customSegmentSlice: customSegmentReducer,
	reportsSlice: reportsReducer,
	userGuideSlice: userGuideReducer,
	askKuralSlice: askKuralReducer,
	profileContextSlice: profileContextReducer,
	tenantsSlice,
	auditSlice,
	accessSlice,
	releasesSlice,
	authSlice,
	// Preview-only slices (branding live-preview carousel — Insights + Ask Kural slides).
	deepDiveInsightslice: deepDiveInsightsReducer,
	sectionalSummarySlice: sectionalSummaryReducer,
	countWidgetSlice: countWidgetReducer,
	widgetSlice: widgetReducer,
	governanceSlice: governanceReducer,
});

const persistConfig = {
	key: "root",
	version: 1,
	storage,
	// Only branding survives reloads; the lifted preview slices are transient.
	whitelist: ["branding"],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
	reducer: persistedReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
			},
		}),
});

// Drop stale, non-whitelisted slices left in localStorage by a previously deployed
// build before rehydrating. redux-persist's whitelist only gates what we *write*;
// on rehydrate autoMergeLevel1 merges every key present in `persist:root` back into
// state. An earlier build here (dev-v4) persisted slices such as `studiesSlice` as
// LZString-compressed strings via a compression transform this build no longer has,
// so those raw strings would rehydrate as slice state and crash the first reducer that
// mutates them (observed: studiesSlice.setMetaDetails — "Cannot create property
// 'metaDetails' on string"). Strip anything outside the whitelist so only `branding`
// survives; if the stored blob is unreadable, purge it entirely. Runs synchronously
// before persistStore's async read, so the stale state never reaches a reducer.
const sanitizePersistedRootState = () => {
	const persistKey = `persist:${persistConfig.key}`;
	try {
		const raw = localStorage.getItem(persistKey);
		if (!raw) return;
		const rootMap = JSON.parse(raw) as Record<string, string>;
		const allowed = new Set<string>([...persistConfig.whitelist, "_persist"]);
		const staleKeys = Object.keys(rootMap).filter((key) => !allowed.has(key));
		if (staleKeys.length === 0) return;
		staleKeys.forEach((key) => delete rootMap[key]);
		localStorage.setItem(persistKey, JSON.stringify(rootMap));
	} catch (error) {
		console.warn("Purging unreadable persisted state; app will cold-start from initial state.", error);
		try {
			localStorage.removeItem(persistKey);
		} catch {
			/* localStorage unavailable — nothing to purge */
		}
	}
};

sanitizePersistedRootState();

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
